import { Button, type SxProps } from '@mui/material';
import { PathType, type InventoryResource } from '@repo/api-gw-sdk';
import classNames from 'classnames';

import { useRoles } from '@/contexts/useRoles';
import { useRouting } from '@/contexts/useRouting';
import { base64UrlEncode } from '@/utils/string';

import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

function trimLocation(location: string): string {
  // Trims the location to 45 characters, puts ellipsis in the middle
  if (location.length <= 45) {
    return location;
  }
  return `${location.slice(0, 20)}...${location.slice(-25)}`;
}

export const PathLink = ({
  entity,
  path,
  pathType,
}: {
  entity: InventoryResource;
  path: string;
  pathType: PathType;
}) => {
  return pathType === PathType.File ? (
    <FileLink
      entity={entity}
      location={path}
      isWindowsMachine={isWindowsMachine([path])}
    />
  ) : (
    <DirLink entity={entity} location={path} />
  );
};

export function FileLink({
  entity,
  location,
  isWindowsMachine,
  sx,
}: {
  entity: InventoryResource;
  location: string;
  isWindowsMachine: boolean;
  sx?: SxProps;
}) {
  const routing = useRouting();
  if (isWindowsMachine) {
    if (location.startsWith('/')) {
      location = location.substring(1);
    }

    location = location.replace(/\//g, '\\');
  }

  return (
    <BasicLink
      sx={sx}
      entity={entity}
      label={location}
      onClick={() => {
        routing.push(
          `/explorer/${encodeURIComponent(entity.id)}?file=${base64UrlEncode(location)}`
        );
      }}
    />
  );
}

export function DirLink({
  entity,
  location,
  sx,
}: {
  entity: InventoryResource;
  location: string;
  sx?: SxProps;
}) {
  const routing = useRouting();
  const onClick = () => {
    routing.push(
      `/explorer/${encodeURIComponent(entity.id)}?dir=${base64UrlEncode(location)}`
    );
  };

  return (
    <BasicLink entity={entity} label={location} onClick={onClick} sx={sx} />
  );
}

const BasicLink = ({
  entity,
  label,
  onClick,
  sx,
}: {
  entity: InventoryResource;
  label: string;
  onClick: () => void;
  sx?: SxProps;
}) => {
  const { isAuthorizedResource } = useRoles();

  return (
    <AuthorizationTooltip
      isAuthorized={isAuthorizedResource('read:files', entity)}
    >
      <span>
        <Button
          variant='text'
          sx={sx}
          data-testid='file-explorer-link'
          className={classNames([
            'p-0',
            'font-semibold',
            {
              'cursor-pointer': isAuthorizedResource('read:files', entity),
            },
          ])}
          disabled={!isAuthorizedResource('read:files', entity)}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
        >
          {trimLocation(label) ?? 'Unknown'}
        </Button>
      </span>
    </AuthorizationTooltip>
  );
};

export const isWindowsMachine = (examples: string[]) =>
  examples
    .filter(Boolean)
    .some((example) =>
      [
        '/C:/',
        '/D:/',
        '/E:/',
        '/F:/',
        'C:/',
        'D:/',
        'E:/',
        'F:/',
        'C:\\',
        'D:\\',
        'E:\\',
        'F:\\',
      ].some((prefix) => example.toUpperCase()?.startsWith(prefix))
    );
